import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { ReactNode, useRef } from 'react';

import { loginRequest } from '@/app/config/auth/msalConfig';
import { msalInstance } from '@/app/config/auth/msalInstance';
import { AuthContext, AuthContextType } from '@/auth/AuthContext';

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider>{children}</AuthContextProvider>
    </MsalProvider>
  );
};

type AuthContextProviderProps = {
  children: ReactNode;
};

const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLoggingOut = useRef(false);

  const user = accounts[0] || null;

  const login = async () => {
    if (isLoggingOut.current) return;

    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      isLoggingOut.current = true;
      instance.setActiveAccount(null);

      const accounts = instance.getAllAccounts();
      accounts.forEach((account) => {
        instance.clearCache({
          account: account,
        });
      });

      // Redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    inProgress,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
